import { useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';

import { formatPhoneNumber } from '../../../Utils/phoneUtils';
import { useTheme } from '../../../Context/ThemeContext';
import { useLanguage } from '../../../Context/LanguageContext';

import CustomScrollbar from '../../Share/Normal/CustomScrollbar';

// Image //
import NormalSortButtonPath from '../../../image/GrayNormalSortButton2.svg';
import NormalSortButtonDarkPath from '../../../image/GrayNormalSortButtonDark2.svg';
import AscSortButtonPath from '../../../image/AscSortButton2.svg';
import AscSortButtonDarkPath from '../../../image/AscSortButtonDark2.svg';
import DescSortButtonPath from '../../../image/DescSortButton2.svg';
import DescSortButtonDarkPath from '../../../image/DescSortButtonDark2.svg';
// Image //

export default function AttendanceRankingList(props) {
  const { data, selectedPartnerGym, calcDateText } = props;

  const history = useHistory();
  const { isDark } = useTheme();
  const { t, languageMode } = useLanguage();

  const [rankSortState, setRankSortState] = useState('normal');
  const [sortedData, setSortedData] = useState([]);

  const scrollRef = useRef(null);

  // 순위 정렬 적용 //
  useEffect(() => {
    let sorted = data.slice();

    if (rankSortState === 'asc') {
      sorted.sort((a, b) => a.attendanceCount - b.attendanceCount);
    }
    else if (rankSortState === 'desc') {
      sorted.sort((a, b) => b.attendanceCount - a.attendanceCount);
    }

    setSortedData(sorted);
  }, [data, rankSortState]);

  // 정렬 상태 토글: normal → asc → desc → normal //
  function onClickRankSort() {
    if (rankSortState === 'normal') setRankSortState('asc');
    else if (rankSortState === 'asc') setRankSortState('desc');
    else setRankSortState('normal');
  }

  // 정렬 버튼 이미지 //
  function getSortButtonImg(state) {
    if (state === 'asc') return isDark ? AscSortButtonDarkPath : AscSortButtonPath;
    if (state === 'desc') return isDark ? DescSortButtonDarkPath : DescSortButtonPath;
    return isDark ? NormalSortButtonDarkPath : NormalSortButtonPath;
  }

  // 생년월일 포맷 //
  function formatBirthDay(bd) {
    const y = bd.slice(0, 4), m = bd.slice(4, 6), d = bd.slice(6, 8);
    const order = t('statistics.calendar.dateOrder');
    const sep = t('statistics.calendar.dateSeparator');

    if (order === 'MDY') return m + sep + d + sep + y;
    return y + sep + m + sep + d;
  }

  // 전화번호 표시 //
  function calcPhone(viewData) {
    if (!viewData.phone || viewData.phone === '') return t('statistics.noPhone');
    return formatPhoneNumber(viewData.phone, viewData.countryCode, languageMode) || t('statistics.noPhone');
  }

  // 회원 정보 클릭시 //
  function onClickMemberInfo(data) {
    if (selectedPartnerGym === undefined || selectedPartnerGym.isMyStore) window.open('/member/valid/detail/' + data.memberId, '_blank');
    else window.open('/member/partner_gym/detail/' + data.memberId, '_blank');
  }

  return (
    <TopLevelWrapper>
      <TitleText>{calcDateText() + ' ' + t('statistics.attendance.attendanceRanking')}</TitleText>
      <BodyWrapper>
        <HeadRow>
          <HeadTd $width={100}>
            {t('statistics.attendance.colRank')}
            <SortButton onClick={onClickRankSort}>
              <SortIcon src={getSortButtonImg(rankSortState)} />
            </SortButton>
          </HeadTd>
          <HeadTd $width={190}>{t('statistics.attendance.colName')}</HeadTd>
          <HeadTd $width={150}>{t('statistics.attendance.colGender')}</HeadTd>
          <HeadTd $width={190}>{t('statistics.attendance.colBirth')}</HeadTd>
          <HeadTd $width={200}>{t('statistics.attendance.colPhone')}</HeadTd>
          <HeadTd $width={100}>{t('statistics.attendance.colAttendanceCount')}</HeadTd>
        </HeadRow>
        <HeadDivisionLine />
        <BodyScrollArea>
          <Body ref={scrollRef}>
            {
              sortedData.map((member, index) => (
                <BodyTr key={'AttendanceRanking-' + index} onClick={() => onClickMemberInfo(member)}>
                  <BodyTd $width={100}>{index + 1}</BodyTd>
                  <BodyTd $width={190} $ellipsis>{member.name}</BodyTd>
                  <BodyTd $width={150}>{member.gender ? (member.gender === 'male' ? t('statistics.attendance.male') : t('statistics.attendance.female')) : '-'}</BodyTd>
                  <BodyTd $width={190}>{formatBirthDay(member.birthDay)}</BodyTd>
                  <BodyTd $width={200}>{calcPhone(member)}</BodyTd>
                  <BodyTd $width={100}>{member.attendanceCount}</BodyTd>
                </BodyTr>
              ))
            }
          </Body>
          <CustomScrollbar
            direction="vertical"
            scrollRef={scrollRef}
            thumbSize={66}
            showTrackAlways={false}
          />
        </BodyScrollArea>
      </BodyWrapper>
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  margin-top: 20px;
  
  gap: 10px;
`;

const TitleText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0;
`;

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 7px;
`;

const HeadRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 20px 0 10px 10px;

  width: calc(100% - 10px);
`;

const HeadTd = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  ${props => props.$width ? `width: ${props.$width}px;` : ''}
`;

const HeadDivisionLine = styled.div`
  width: 100%;
  height: 1px;

  opacity: 0.5;
  background-color: ${props => props.theme.line};
`;

const BodyScrollArea = styled.div`
  display: flex;
  flex-direction: row;

  width: 100%;
  height: 332px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: 100%;

  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`;

const BodyTr = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 10px 0 10px 10px;

  width: calc(100% - 10px);

  border-radius: 5px;
  background-color: transparent;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.subKey};
  }

  &:hover > div {
    color: ${props => props.theme.subBackground};
  }
`;

const BodyTd = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  ${props => props.$width ? `width: ${props.$width}px;` : ''}
  ${props => props.$flex ? `flex: ${props.$flex};` : ''}

  overflow: ${props => props.$ellipsis ? 'hidden' : 'visible'};
  white-space: ${props => props.$ellipsis ? 'nowrap' : 'normal'};
  text-overflow: ${props => props.$ellipsis ? 'ellipsis' : 'clip'};

  transition: color 0.3s ease-in-out;
`;

const SortButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  margin-left: 5px;

  cursor: pointer;
`;

const SortIcon = styled.img`
  width: 20px;
  height: 20px;
`;
